/**
 * Zeus stacking cards — active card, buried dim, video IO, pinned GSAP scroll stage.
 * Uses zeus-gsap / ScrollTrigger when available; vanilla IO fallback otherwise.
 */
(function () {
  var GSAP_WAIT_TIMEOUT_MS = 6000;

  function waitForGsap(callback, onGiveUp) {
    if (typeof window.zeusWhenGsapReady === "function") {
      window.zeusWhenGsapReady(callback, onGiveUp);
      return;
    }
    if (window.gsap && window.ScrollTrigger) {
      callback();
      return;
    }

    var timeoutId;
    function finish() {
      window.clearTimeout(timeoutId);
      callback();
    }
    window.addEventListener("zeus:gsap-ready", finish, { once: true });
    timeoutId = window.setTimeout(function () {
      window.removeEventListener("zeus:gsap-ready", finish);
      if (typeof onGiveUp === "function") onGiveUp();
    }, GSAP_WAIT_TIMEOUT_MS);
  }

  function prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function isMobile() {
    return window.matchMedia("(max-width: 699px)").matches;
  }

  function headerStickyTop() {
    var headerHeight =
      parseInt(getComputedStyle(document.documentElement).getPropertyValue("--header-height"), 10) || 72;
    return headerStickyTop._value || (headerStickyTop._value = headerHeight + 8);
  }

  function preloadStageUrl(url) {
    if (!url) {
      return;
    }
    var img = new Image();
    img.src = url;
  }

  function setStageBackground(root, index) {
    var layers = root.querySelectorAll("[data-zeus-stack-bg-layer]");
    if (!layers.length) {
      return;
    }

    var transition = root.getAttribute("data-stage-transition") || "crossfade";
    var instant = transition === "instant" || prefersReducedMotion();
    var gsap = window.gsap;
    var useGsap = !instant && !isMobile() && gsap && root.classList.contains("is-gsap");

    layers.forEach(function (layer) {
      var layerIndex = parseInt(layer.getAttribute("data-index"), 10);
      var active = layerIndex === index;
      layer.classList.toggle("is-active", active);
      layer.setAttribute("aria-hidden", active ? "false" : "true");

      if (useGsap) {
        gsap.to(layer, {
          opacity: active ? 1 : 0,
          duration: 0.6,
          ease: "power2.inOut",
          overwrite: true,
        });
      } else if (instant && gsap) {
        gsap.set(layer, { opacity: active ? 1 : 0 });
      }
    });

    var wraps = root.querySelectorAll(".zeus-stack__wrap");
    var nextWrap = wraps[index + 1];
    var prevWrap = wraps[index - 1];
    if (nextWrap && nextWrap.dataset.stageBgUrl) {
      preloadStageUrl(nextWrap.dataset.stageBgUrl);
    }
    if (prevWrap && prevWrap.dataset.stageBgUrl) {
      preloadStageUrl(prevWrap.dataset.stageBgUrl);
    }
  }

  function setActive(root, wraps, dots, index) {
    wraps.forEach(function (wrap, i) {
      var card = wrap.querySelector(".zeus-stack-card");
      var buried = i < index;
      var active = i === index;

      wrap.classList.toggle("is-buried", buried);
      wrap.classList.toggle("is-active", active);

      if (card) {
        if (active) {
          card.setAttribute("aria-current", "true");
        } else {
          card.removeAttribute("aria-current");
        }
      }
    });

    dots.forEach(function (dot, i) {
      var active = i === index;
      dot.classList.toggle("is-active", active);
      if (active) {
        dot.setAttribute("aria-current", "step");
      } else {
        dot.removeAttribute("aria-current");
      }
    });

    setStageBackground(root, index);
  }

  function initVideos(root) {
    if (root.dataset.zeusStackVideosInit) return;
    root.dataset.zeusStackVideosInit = "1";
    var videos = root.querySelectorAll("[data-zeus-stack-video]");
    if (prefersReducedMotion()) {
      videos.forEach(function (video) {
        video.pause();
      });
      return;
    }
    if (!videos.length || typeof IntersectionObserver === "undefined") {
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting && !video.dataset.zeusSourceLoaded) {
            video.querySelectorAll("source[data-src]").forEach(function (source) {
              source.src = source.dataset.src;
            });
            video.dataset.zeusSourceLoaded = "true";
            video.load();
          }
          if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
            var playPromise = video.play();
            if (playPromise && typeof playPromise.catch === "function") {
              playPromise.catch(function () {});
            }
          } else {
            video.pause();
          }
        });
      },
      { threshold: [0, 0.2, 0.35, 0.55] },
    );

    videos.forEach(function (video) {
      video.muted = true;
      observer.observe(video);
    });
  }

  function getStackParts(root) {
    return {
      wraps: Array.prototype.slice.call(root.querySelectorAll(".zeus-stack__wrap")),
      dots: Array.prototype.slice.call(root.querySelectorAll(".zeus-stack__progress-dot")),
    };
  }

  /**
   * Static fallback (reduced motion / no GSAP): later cards slide over earlier
   * ones, so a focused link can sit under the next card. Scroll the focused
   * card back to the point where it is on top of the sticky pile (ZRT-141).
   */
  function initStaticFocus(root, wraps) {
    var stack = root.querySelector(".zeus-stack");
    if (!stack || stack.dataset.zeusStackStaticFocus) {
      return;
    }
    stack.dataset.zeusStackStaticFocus = "1";
    stack.addEventListener("focusin", function (event) {
      var target = event.target;
      if (root.classList.contains("is-gsap") || !target.matches || !target.matches(":focus-visible")) {
        return;
      }
      var index = wraps.findIndex(function (wrap) {
        return wrap.contains(target);
      });
      if (index < 0) {
        return;
      }
      var offset = 0;
      for (var i = 0; i < index; i += 1) {
        offset += wraps[i].offsetHeight;
      }
      var stuckTop = parseFloat(getComputedStyle(wraps[index]).top) || 0;
      var top = stack.getBoundingClientRect().top + window.scrollY + offset - stuckTop;
      window.requestAnimationFrame(function () {
        window.scrollTo({ top: top, behavior: "instant" });
      });
    });
  }

  function initActiveTracking(root, options) {
    options = options || {};
    var parts = getStackParts(root);
    var wraps = parts.wraps;
    var dots = parts.dots;
    if (!wraps.length) {
      return null;
    }

    setActive(root, wraps, dots, 0);
    initStaticFocus(root, wraps);

    if (options.io === false || typeof IntersectionObserver === "undefined") {
      return null;
    }

    var ratios = new Map();

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            ratios.set(entry.target, entry.intersectionRatio);
          } else {
            ratios.delete(entry.target);
          }
        });

        var bestIndex = 0;
        var bestRatio = -1;

        ratios.forEach(function (ratio, wrap) {
          var index = wraps.indexOf(wrap);
          if (index >= 0 && ratio > bestRatio) {
            bestRatio = ratio;
            bestIndex = index;
          }
        });

        if (bestRatio >= 0) {
          setActive(root, wraps, dots, bestIndex);
        }
      },
      { threshold: [0.15, 0.35, 0.55, 0.75], rootMargin: "-12% 0px -38% 0px" },
    );

    wraps.forEach(function (wrap) {
      observer.observe(wrap);
    });

    return observer;
  }

  /*
   * Pinned stage timing, mirrored from the Nivis reference (products showcase):
   * a short hold after pinning, then each card leaves upward over one step
   * while the next one rises and scales in between 25% and 75% of that step.
   */
  var STACK_HOLD = 0.3;
  var STACK_STEP = 1.4;

  /** Desktop: layered deck, the front card leaves upward, the next one settles. */
  function buildDeckTimeline(gsap, wraps, viewport, stack, stickyTop, onUpdate) {
    var offset = 12;
    var lastSteps = Math.max(1, wraps.length - 1);

    wraps.forEach(function (wrap, index) {
      gsap.set(wrap, {
        y: index * offset,
        scale: Math.max(0.9, 1 - index * 0.02),
        transformOrigin: "50% 50%",
      });
    });

    var timeline = gsap.timeline({
      scrollTrigger: {
        trigger: stack,
        start: "top " + stickyTop + "px",
        end: function () {
          return "+=" + lastSteps * viewport.getBoundingClientRect().height;
        },
        scrub: 1.2,
        invalidateOnRefresh: true,
        onUpdate: onUpdate,
      },
    });

    timeline.to({}, { duration: STACK_HOLD });

    wraps.forEach(function (wrap, index) {
      if (index === wraps.length - 1) {
        return;
      }
      var at = STACK_HOLD + index * STACK_STEP;
      var settleAt = at + STACK_STEP * 0.25;

      timeline.to(
        wrap,
        {
          y: function () {
            return -viewport.getBoundingClientRect().height;
          },
          ease: "none",
          duration: STACK_STEP,
        },
        at,
      );
      timeline.to(wraps[index + 1], { y: 0, scale: 1, ease: "none", duration: STACK_STEP * 0.5 }, settleAt);

      for (var next = index + 2; next < wraps.length; next += 1) {
        var depth = next - (index + 1);
        timeline.to(
          wraps[next],
          { y: offset * depth, scale: Math.max(0.9, 1 - depth * 0.02), ease: "none", duration: STACK_STEP * 0.5 },
          settleAt,
        );
      }
    });

    timeline.to({}, { duration: STACK_HOLD });

    return {
      timeline: timeline,
      /* Timeline time where card i is fully settled, before it starts leaving. */
      settledTime: function (index) {
        return index === 0 ? STACK_HOLD : STACK_HOLD + (index - 1) * STACK_STEP + STACK_STEP * 0.9;
      },
      activeIndex: function (time) {
        var moving = Math.min(Math.max(time - STACK_HOLD, 0), lastSteps * STACK_STEP);
        return Math.round((moving / (lastSteps * STACK_STEP)) * lastSteps);
      },
    };
  }

  /**
   * Mobile: no deck, the cards travel through the pinned stage as one strip.
   * The Nivis gap is 60px, but the step is never shorter than the space above
   * the centred card: otherwise the card that just left keeps a sliver at the
   * top of the stage, with an empty band under it (ZRT-145).
   */
  function buildSlideTimeline(gsap, wraps, viewport, stack, stickyTop, onUpdate) {
    var gap = 60;
    var lastSteps = wraps.length - 1;
    var travel = function () {
      var card = wraps[0].querySelector(".zeus-stack-card");
      if (!card) {
        return viewport.offsetHeight + gap;
      }
      return card.offsetHeight + Math.max(gap, card.offsetTop);
    };

    var timeline = gsap.timeline({
      scrollTrigger: {
        trigger: stack,
        start: "top " + stickyTop + "px",
        end: function () {
          return "+=" + lastSteps * viewport.getBoundingClientRect().height;
        },
        scrub: true,
        invalidateOnRefresh: true,
        onUpdate: onUpdate,
      },
    });

    /* Card i sits i steps below the stage and is settled at time i.
       A single card has nowhere to go: it stays on stage. */
    (lastSteps > 0 ? wraps : []).forEach(function (wrap, index) {
      timeline.fromTo(
        wrap,
        {
          y: function () {
            return index * travel();
          },
          scale: 1,
        },
        {
          y: function () {
            return (index - lastSteps) * travel();
          },
          ease: "none",
          duration: lastSteps,
        },
        0,
      );
    });

    return {
      timeline: timeline,
      settledTime: function (index) {
        return index;
      },
      activeIndex: function (time) {
        return Math.min(lastSteps, Math.floor(time + 0.4));
      },
    };
  }

  /**
   * One pinned stage under the header. Desktop and mobile get the two Nivis
   * motions; gsap.matchMedia rebuilds them when the breakpoint changes.
   */
  function initPinnedStackMotion(root, wraps, dots, stickyTop) {
    var gsap = window.gsap;
    var stack = root.querySelector(".zeus-stack");
    var viewport = root.querySelector("[data-zeus-stack-viewport]");
    var fill = root.querySelector("[data-zeus-stack-progress-fill]");

    if (!stack || !viewport || !wraps.length) {
      return;
    }

    var motion = null;
    var activeIndex = 0;

    function onUpdate(self) {
      if (!motion) {
        return;
      }
      var nextIndex = motion.activeIndex(self.progress * motion.timeline.duration());
      if (nextIndex !== activeIndex) {
        activeIndex = nextIndex;
        setActive(root, wraps, dots, activeIndex);
      }
      if (fill) {
        gsap.set(fill, { scaleY: self.progress });
      }
    }

    gsap.matchMedia().add({ mobile: "(max-width: 699px)", desktop: "(min-width: 700px)" }, function (context) {
      var build = context.conditions.mobile ? buildSlideTimeline : buildDeckTimeline;
      motion = build(gsap, wraps, viewport, stack, stickyTop, onUpdate);
      return function () {
        motion = null;
      };
    });

    /* The rising entrance of the whole deck as the section scrolls in. */
    var entrance = gsap.from(viewport, {
      yPercent: 100,
      ease: "power2.out",
      scrollTrigger: {
        trigger: stack,
        start: "top 85%",
        end: "top center",
        scrub: 3,
        invalidateOnRefresh: true,
      },
    });

    /* Cards share one pinned stage, so chapter anchors cannot scroll to them:
       resolve each card to the scroll offset where it is settled. */
    function settledScroll(index) {
      var trigger = motion && motion.timeline.scrollTrigger;
      if (!trigger) {
        return null;
      }
      var progress = motion.settledTime(index) / (motion.timeline.duration() || 1);
      return trigger.start + (trigger.end - trigger.start) * progress;
    }

    dots.forEach(function (dot, index) {
      dot.addEventListener("click", function (event) {
        var top = settledScroll(index);
        if (top === null) {
          return;
        }
        event.preventDefault();
        window.scrollTo({ top: top, behavior: "smooth" });
      });
    });

    /* Every card link stays in the tab order (mobile has no chapter nav).
       Keyboard focus on one brings its card to its settled place at once
       (no scrub lag), so the focus ring is never under the header or behind
       the stage edge (ZRT-141). */
    stack.addEventListener("focusin", function (event) {
      var target = event.target;
      if (!target.matches || !target.matches(":focus-visible")) {
        return;
      }
      var index = wraps.findIndex(function (wrap) {
        return wrap.contains(target);
      });
      var top = index < 0 ? null : settledScroll(index);
      if (top === null) {
        return;
      }
      window.requestAnimationFrame(function () {
        window.scrollTo({ top: top, behavior: "instant" });
        window.ScrollTrigger.update();
        [motion && motion.timeline.scrollTrigger, entrance.scrollTrigger].forEach(function (trigger) {
          var scrub = trigger && trigger.getTween && trigger.getTween();
          if (scrub) {
            scrub.progress(1);
          }
        });
      });
    });
  }

  function initGsap(root) {
    var gsap = window.gsap;
    var parts = getStackParts(root);
    var wraps = parts.wraps;
    var dots = parts.dots;
    var stickyTop = headerStickyTop();

    root.classList.add("is-gsap");

    if (wraps.length) {
      setActive(root, wraps, dots, 0);
      initPinnedStackMotion(root, wraps, dots, stickyTop);
    }

    var intro = root.querySelector(".zeus-stack-section__intro");
    var hasHeroCinema = !!document.querySelector("[data-zeus-hero-cinema]");

    if (intro && !hasHeroCinema) {
      gsap.fromTo(
        intro,
        { opacity: 0, y: 32 },
        {
          opacity: 1,
          y: 0,
          ease: "power2.out",
          scrollTrigger: {
            trigger: root,
            start: "top 88%",
            end: "top 52%",
            scrub: 0.65,
            invalidateOnRefresh: true,
          },
        },
      );
    }
  }

  function mountStagePhotos(root) {
    if (root.dataset.zeusStackStageInit) return;
    root.dataset.zeusStackStageInit = "1";
    if (root.querySelector("[data-zeus-stack-bg]")) {
      return;
    }

    var template = root.querySelector("[data-zeus-stack-bg-template]");
    var slot = root.querySelector(".zeus-stack-section__bg");
    if (!template || !slot || !template.content) {
      return;
    }

    slot.insertBefore(template.content.cloneNode(true), slot.firstChild);
  }

  function shouldUseGsap() {
    /* Sticky stack + ScrollTrigger on all viewports (mobile restored sticky CSS). */
    return !prefersReducedMotion();
  }

  function boot() {
    var roots = document.querySelectorAll("[data-zeus-stacking-cards]");
    if (!roots.length) {
      return;
    }

    roots.forEach(function (root) {
      var initialize = function () {
        if (root.dataset.zeusStackMotionInit === "true") return;
        root.dataset.zeusStackMotionInit = "true";

        mountStagePhotos(root);
        initVideos(root);

        if (!shouldUseGsap()) {
          initActiveTracking(root);
          return;
        }

        var parts = getStackParts(root);
        if (parts.wraps.length) {
          setActive(root, parts.wraps, parts.dots, 0);
        }

        waitForGsap(
          function () {
            window.gsap.registerPlugin(window.ScrollTrigger);
            initGsap(root);
          },
          function () {
            root.dataset.zeusStackMotionInit = "false";
            root.classList.remove("is-gsap");
            initActiveTracking(root);
          },
        );
      };

      if (typeof window.zeusWhenNearViewport === "function") {
        window.zeusWhenNearViewport(root, initialize);
      } else {
        initialize();
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
